/**
 * Compact dropdown that lets the user pick which comparison period the
 * dashboard KPIs use (this week vs last week, this month vs last year, …).
 */
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@erp/ui';
import { Calendar } from '@/components/icons';
import { PERIOD_MODES, type PeriodMode } from './period-range';

interface PeriodComparisonSelectorProps {
  value: PeriodMode;
  onChange: (mode: PeriodMode) => void;
  className?: string;
}

const MODE_TO_KEY: Record<PeriodMode, string> = {
  'day-vs-yesterday': 'dashboard.compare.modes.dayVsYesterday',
  'day-vs-last-year': 'dashboard.compare.modes.dayVsLastYear',
  'week-vs-last-week': 'dashboard.compare.modes.weekVsLastWeek',
  'week-vs-last-year': 'dashboard.compare.modes.weekVsLastYear',
  'month-vs-last-month': 'dashboard.compare.modes.monthVsLastMonth',
  'month-vs-last-year': 'dashboard.compare.modes.monthVsLastYear',
  'year-vs-last-year': 'dashboard.compare.modes.yearVsLastYear',
};

export function PeriodComparisonSelector({
  value,
  onChange,
  className,
}: PeriodComparisonSelectorProps) {
  const { t } = useTranslation();
  return (
    <div className={`flex min-w-0 items-center gap-2 ${className ?? ''}`}>
      <Calendar size={14} className="shrink-0 text-muted-foreground" aria-hidden />
      <Select value={value} onValueChange={(v) => onChange(v as PeriodMode)}>
        <SelectTrigger
          size="sm"
          className="h-8 w-full min-w-0 text-xs lg:w-auto lg:min-w-[14rem]"
          aria-label={t('dashboard.compare.title')}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PERIOD_MODES.map((mode) => (
            <SelectItem key={mode} value={mode} className="text-xs">
              {t(MODE_TO_KEY[mode])}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
